import React, { useState, useRef, useEffect } from 'react';
import './AIChat.css';

export default function AIChat({ onClose, onBack, onSendMessage, messages: externalMessages = [], loading = false, error = null, conversationId: propConversationId = null, showHeader = false }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Xin chào! Tôi là trợ lý sức khỏe tinh thần AI của bạn. Tôi ở đây để lắng nghe và giúp bạn xử lý những suy nghĩ và cảm xúc của mình. Hôm nay bạn cảm thấy thế nào?",
      timestamp: "10:25",
      avatar: "🤖"
    }
  ]);

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Format timestamps to a friendly local representation.
  // - If timestamp is today, show time only (HH:mm)
  // - If same year, show "Mon dd HH:mm"
  // - Otherwise include year
  // Also trim microsecond precision to milliseconds so `new Date()` can parse it.
  const formatTimestamp = (ts) => {
    if (!ts && ts !== 0) return '';
    try {
      let t = ts;
      if (typeof t === 'string') {
        // Trim excessive fractional seconds (e.g. .725045 -> .725)
        t = t.replace(/(\.\d{3})\d+/, '$1');
      }
      const d = (t instanceof Date) ? t : new Date(t);
      if (isNaN(d)) return String(ts);
      const now = new Date();
      const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
      if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      if (d.getFullYear() === now.getFullYear()) return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
      return d.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch (e) {
      return String(ts);
    }
  };

  useEffect(() => {
    if (externalMessages && externalMessages.length > 0) {
      setMessages(externalMessages);
    }
  }, [externalMessages]);

  // Sync conversationId prop from parent so AIChat knows which conversation to use
  useEffect(() => {
    if (propConversationId) setConversationId(propConversationId);
  }, [propConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    const newMessageLocal = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      isVoice: false
    };

    // Optimistically append the user's message so UI updates immediately
    setMessages(prev => [...prev.filter(msg => !msg.isTyping), newMessageLocal]);
    setInputMessage('');
    setIsTyping(true);

    if (typeof onSendMessage === 'function') {
      try {
        // Allow parent to process the message and optionally return a response
        const response = await onSendMessage(inputMessage, { type: 'text', conversationId });

        // If the parent returns an AI response string or object, append it locally
        if (response) {
          // Update conversationId if backend returned one
          const respConversationId = response?.ConversationId || response?.conversationId || response?.conversation_id;
          if (respConversationId) setConversationId(respConversationId);

          let aiContent = '';
          if (typeof response === 'string') aiContent = response;
          else if (typeof response === 'object') {
            aiContent = response?.AssistantText || response?.assistantText || response?.assistant_text || response?.content || response?.Content || '';
          }

          if (aiContent) {
            const aiResponse = {
              id: Date.now() + 1,
              type: 'ai',
              content: aiContent,
              timestamp: new Date().toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }),
              avatar: "🤖"
            };
            setMessages(prev => [...prev, aiResponse]);
          }
        }

        // If parent will push `externalMessages`, that effect will replace messages; still clear typing
        setIsTyping(false);
      } catch (err) {
        // fallback: show a local mock response on error
        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          content: "Xin lỗi, tôi gặp lỗi khi gửi tin nhắn. Vui lòng thử lại.",
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          avatar: "🤖"
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }
    } else {
      // Local mock behaviour when no parent handler provided
      // Simulate AI response
      setTimeout(() => {
        const aiResponses = [
          "Tôi hiểu cảm giác của bạn. Việc có những suy nghĩ này là hoàn toàn bình thường. Bạn có thể kể cho tôi nghe thêm về những gì đang đè nặng trong tâm trí bạn gần đây không?",
          "Nghe có vẻ thực sự khó khăn. Cần có can đảm để chia sẻ những cảm xúc này. Bạn nghĩ điều gì có thể giúp bạn cảm thấy tốt hơn ngay bây giờ?",
          "Tôi nghe bạn, và tôi muốn bạn biết rằng cảm xúc của bạn là hợp lệ. Đôi khi việc chia nhỏ mọi thứ thành các bước nhỏ hơn sẽ giúp ích. Hôm nay bạn có thể làm một việc nhỏ nào cho bản thân?",
          "Cảm ơn bạn đã tin tưởng tôi với điều này. Không sao nếu bạn chưa có tất cả câu trả lời ngay bây giờ. Tôi có thể hỗ trợ bạn tốt nhất như thế nào trong lúc này?",
          "Tôi có thể cảm nhận điều này thực sự quan trọng với bạn. Hãy cùng khám phá điều này. Bạn muốn tập trung vào điều gì trước?"
        ];

        const randomResponse = aiResponses[Math.floor(Math.random() * aiResponses.length)];

        const aiResponse = {
          id: Date.now() + 1,
          type: 'ai',
          content: randomResponse,
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          avatar: "🤖"
        };
        setMessages(prev => [...prev, aiResponse]);
        setIsTyping(false);
      }, 1200);
    }
  };

  const handleVoiceMessage = () => {
    const voiceMessage = {
      id: messages.length + 1,
      type: 'user',
      content: "",
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      isVoice: true,
      duration: "01:30"
    };

    setMessages(prev => [...prev.filter(msg => !msg.isTyping), voiceMessage]);
  };

  return (
    <div className="ai-chat-interface">
      {showHeader && (
        <div className="ai-chat-header">
          <div className="ai-chat-title">
            {onBack && (
              <button className="back-btn" onClick={onBack}>←</button>
            )}
            <span className="ai-avatar">🤖</span>
            <div className="ai-chat-info">
              <h3>Trợ lý AI Chat</h3>
              <div className="ai-status">Luôn trực tuyến</div>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
      )}

      <div className="ai-chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`ai-message ${message.type}`}>
            {message.type === 'ai' && (
              <div className="ai-message-content">
                <div className="ai-avatar-bubble">🤖</div>
                <div className="ai-message-bubble">
                  <p>{message.content}</p>
                  <div className="ai-message-actions">
                    <button className="ai-action-btn">🔊</button>
                    <button className="ai-action-btn">📤</button>
                    <button className="ai-action-btn">👍</button>
                    <button className="ai-action-btn">👎</button>
                  </div>
                  <span className="ai-timestamp">{formatTimestamp(message.timestamp)}</span>
                </div>
              </div>
            )}

            {message.type === 'user' && !message.isTyping && (
              <div className="user-message-content">
                <div className="user-message-bubble">
                  {message.isVoice ? (
                    <div className="voice-message">
                      <div className="voice-waveform">
                        <div className="wave"></div>
                        <div className="wave"></div>
                        <div className="wave"></div>
                        <div className="wave"></div>
                        <div className="wave"></div>
                      </div>
                      <span className="voice-duration">{message.duration}</span>
                    </div>
                  ) : (
                    <p>{message.content}</p>
                  )}
                  <span className="user-timestamp">{formatTimestamp(message.timestamp)}</span>
                </div>
                <div className="user-avatar-bubble">👤</div>
              </div>
            )}

            {message.isTyping && (
              <div className="user-message-content">
                <div className="user-message-bubble typing">
                  <div className="typing-dots">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
                <div className="user-avatar-bubble">👤</div>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-chat-input">
        <div className="ai-input-icons">
          <button className="ai-input-icon">😊</button>
          <button className="ai-input-icon">📎</button>
          <button className="ai-input-icon" onClick={handleVoiceMessage}>🎤</button>
        </div>
        <form onSubmit={handleSendMessage} className="ai-input-form">
          <input
            type="text"
            placeholder="Tin nhắn cho Trợ lý AI..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            className="ai-message-input"
          />
          <button type="submit" className="ai-send-btn">
            Gửi
            <span className="ai-send-arrow">→</span>
          </button>
        </form>
      </div>
    </div>
  );
}

